import type { ReactElement } from "react";

import { Card } from "@/components/hifi/Card";
import { Icon, type IconName } from "@/components/hifi/Icon";
import { Pill } from "@/components/hifi/Pill";
import { ShellLayout } from "@/components/shell/ShellLayout";

type Layer = {
  key: "deter" | "detect" | "delay" | "respond";
  label: string;
  blurb: string;
  icon: IconName;
  accent: string;
};

const LAYERS: Layer[] = [
  {
    key: "deter",
    label: "Deter",
    blurb:
      "Signage, lighting, visible patrols, hardened perimeters — reduce adversary motivation before an attempt.",
    icon: "shield",
    accent: "var(--a-600)",
  },
  {
    key: "detect",
    label: "Detect",
    blurb:
      "CCTV, intrusion sensors, access anomalies, guard tours — raise an alarm as early as possible.",
    icon: "target",
    accent: "var(--warn)",
  },
  {
    key: "delay",
    label: "Delay",
    blurb:
      "Doors, locks, barriers, vaults, mantraps — buy time between detection and adversary task completion.",
    icon: "clock",
    accent: "var(--bad)",
  },
  {
    key: "respond",
    label: "Respond",
    blurb:
      "On-site response, LEO coordination, escalation playbooks, lockdown — neutralise the adversary before task completion.",
    icon: "bell",
    accent: "var(--ok)",
  },
];

export function CountermeasuresPage(): ReactElement {
  return (
    <ShellLayout
      active="controls"
      topbar={{
        crumbs: ["Workspace", "Countermeasures"],
        title: "Countermeasures",
        subtitle: "Deter / Detect / Delay / Respond — four-layer physical security model",
      }}
    >
      <div className="space-y-5 p-5">
        <Card>
          <div className="flex items-start gap-3 border-b border-n-150 px-4 py-3">
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-2"
              style={{ background: "var(--a-50)" }}
            >
              <Icon name="sparkle" size={16} color="var(--a-600)" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold text-n-900">
                  Countermeasures module
                </div>
                <Pill variant="accent">Planned</Pill>
              </div>
              <p className="mt-1 text-[12px] text-n-500">
                A dedicated model for physical countermeasures is on the Phase J roadmap.
                Today, treatment decisions live inline on each threat (Step 7 of the Wizard)
                and flow through to the Action Plan tracker.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-4">
            {LAYERS.map((layer) => (
              <div
                key={layer.key}
                className="rounded-3 border border-n-150 bg-white p-3.5"
                style={{ borderLeft: `3px solid ${layer.accent}` }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-2"
                    style={{ background: "var(--n-75)" }}
                  >
                    <Icon name={layer.icon} size={14} color={layer.accent} />
                  </div>
                  <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.4px] text-n-500">
                    {layer.label}
                  </div>
                </div>
                <p className="mt-2 text-[12px] leading-[1.5] text-n-700">
                  {layer.blurb}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Pill>0 controls</Pill>
                  <span className="text-[11px] text-n-400">— not tracked yet</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="border-b border-n-150 px-4 py-2.5">
            <div className="text-[13px] font-semibold text-n-900">
              Where treatments live today
            </div>
          </div>
          <ul className="m-0 list-none space-y-2 p-4 text-[13px] text-n-700">
            <li className="flex items-start gap-2">
              <Icon name="list" size={13} color="var(--n-500)" />
              <span>
                <span className="font-medium text-n-900">
                  Risk treatment per threat
                </span>{" "}
                — Wizard Step 7 (Treatment) captures Accept / Transfer / Mitigate /
                Avoid plus residual-risk estimates.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="checkBox" size={13} color="var(--n-500)" />
              <span>
                <span className="font-medium text-n-900">Action plan tracker</span>{" "}
                — mitigation tasks with owner, due date, priority, status at{" "}
                <a
                  href="/action-plans"
                  className="text-a-600 no-underline hover:underline"
                >
                  /action-plans
                </a>
                .
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="shield" size={13} color="var(--n-500)" />
              <span>
                <span className="font-medium text-n-900">
                  Coming in Phase J
                </span>{" "}
                — a first-class <code className="font-mono text-[11px]">Countermeasure</code>{" "}
                model with asset + threat joins, effectiveness ratings, and
                residual-risk calculations fed back into the Wizard.
              </span>
            </li>
          </ul>
        </Card>
      </div>
    </ShellLayout>
  );
}
